import {
  buildLoginRecoveryPath,
  isAuthFlowPath,
} from "../auth/returnPath";

const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const AUTH_RECOVERY_CODES = new Set([
  "NO_SESSION",
  "SESSION_INVALID",
  "IDENTITY_GONE",
]);

let authRecoveryStarted = false;

let authBridge = {
  getCsrfToken: () => null,
  refresh: null,
  refreshCsrf: null,
  navigateToAuth: null,
};

export function resolveApiBaseUrl(
  configuredUrl = process.env.REACT_APP_API_BASE_URL
) {
  return typeof configuredUrl === "string"
    ? configuredUrl.trim().replace(/\/+$/, "")
    : "";
}

export async function readJsonResponse(response) {
  let json = null;
  let parseFailed = false;

  try {
    json = await response.json();
  } catch {
    parseFailed = true;
  }

  if (!response.ok) {
    const error = new Error(
      json?.error?.message || json?.message || `API error ${response.status}`
    );
    error.code = json?.error?.code || json?.code || null;
    error.details = json?.error?.details || null;
    error.status = response.status;
    error.retryAfter = response.headers?.get?.("Retry-After") || null;
    throw error;
  }

  if (parseFailed) {
    const error = new Error(`Malformed response body (status ${response.status})`);
    error.code = "MALFORMED_RESPONSE";
    error.status = response.status;
    throw error;
  }

  return json;
}

export function configureApiFetchAuth({
  getCsrfToken,
  refresh,
  refreshCsrf,
  navigateToAuth,
} = {}) {
  authBridge = {
    getCsrfToken: typeof getCsrfToken === "function" ? getCsrfToken : () => null,
    refresh: typeof refresh === "function" ? refresh : null,
    refreshCsrf: typeof refreshCsrf === "function" ? refreshCsrf : null,
    navigateToAuth: typeof navigateToAuth === "function" ? navigateToAuth : null,
  };

  return () => {
    authBridge = {
      getCsrfToken: () => null,
      refresh: null,
      refreshCsrf: null,
      navigateToAuth: null,
    };
  };
}

export function resetAuthRecoveryForTests() {
  authRecoveryStarted = false;
}

function beginAuthRecovery(error) {
  if (
    authRecoveryStarted ||
    error?.status !== 401 ||
    !AUTH_RECOVERY_CODES.has(error?.code) ||
    typeof window === "undefined" ||
    isAuthFlowPath(window.location.pathname)
  ) {
    return false;
  }

  authRecoveryStarted = true;
  const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const destination = buildLoginRecoveryPath(returnPath);
  if (authBridge.navigateToAuth) {
    authBridge.navigateToAuth(destination);
  } else {
    window.location.assign(destination);
  }
  return true;
}

function isJsonBody(body) {
  return !(
    body == null ||
    typeof body === "string" ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob
  );
}

function buildHeaders(headers, { method, body, csrfToken }) {
  const nextHeaders = new Headers(headers || {});
  if (isJsonBody(body) && !nextHeaders.has("Content-Type")) {
    nextHeaders.set("Content-Type", "application/json");
  }
  if (STATE_CHANGING_METHODS.has(method) && csrfToken) {
    nextHeaders.set("X-CSRF-Token", csrfToken);
  }
  return nextHeaders;
}

function normalizeBody(body) {
  if (!isJsonBody(body)) {
    return body;
  }
  return JSON.stringify(body);
}

function createResponseError(response, json) {
  const error = new Error(
    json?.error?.message || json?.message || `API error ${response.status}`
  );
  error.code = json?.error?.code || json?.code || null;
  error.details = json?.error?.details || null;
  error.status = response.status;
  error.retryAfter = response.headers?.get?.("Retry-After") || null;
  return error;
}

function createSessionChangedError(cause) {
  const error = new Error("The signed-in account changed.");
  error.code = "SESSION_USER_CHANGED";
  error.details = null;
  error.status = 401;
  error.retryAfter = null;
  error.cause = cause;
  return error;
}

export async function fetchAuthSession({ signal, fetchImpl = fetch } = {}) {
  const response = await fetchImpl(
    `${resolveApiBaseUrl()}/api/auth/session`,
    { credentials: "include", signal }
  );

  let json = null;
  try {
    json = await response.json();
  } catch {
    if (response.ok) {
      const error = new Error(`Malformed response body (status ${response.status})`);
      error.code = "MALFORMED_RESPONSE";
      error.status = response.status;
      throw error;
    }
  }

  if (response.status === 401) {
    return { status: 401, body: json || {} };
  }
  if (!response.ok) {
    throw createResponseError(response, json);
  }
  if (json?.user?.id) {
    authRecoveryStarted = false;
  }
  return { status: response.status, body: json };
}

export async function apiFetch(path, options = {}, internal = {}) {
  const {
    authRecovery = "default",
    csrfRecovery = "default",
    ...requestOptions
  } = options;
  const method = String(requestOptions.method || "GET").toUpperCase();
  const csrfToken = authBridge.getCsrfToken();
  const response = await (internal.fetchImpl || fetch)(
    `${resolveApiBaseUrl()}${path}`,
    {
      ...requestOptions,
      method,
      credentials: "include",
      headers: buildHeaders(requestOptions.headers, {
        method,
        body: requestOptions.body,
        csrfToken,
      }),
      body: normalizeBody(requestOptions.body),
    }
  );

  if (response.status === 204) {
    return null;
  }

  try {
    return await readJsonResponse(response);
  } catch (error) {
    if (authRecovery !== "none") {
      beginAuthRecovery(error);
    }
    const refreshHandler = csrfRecovery === "isolated"
      ? authBridge.refreshCsrf
      : authBridge.refresh;
    const canRefresh =
      !internal.hasRetried &&
      error?.status === 403 &&
      error?.code === "CSRF_TOKEN_INVALID" &&
      typeof refreshHandler === "function";
    if (!canRefresh) {
      throw error;
    }

    const refreshed = await refreshHandler();
    // The browser Session now belongs to another user (Workout requests only).
    // Replaying this request would send it under that user's Session, so it is
    // never retried: it fails as an auth loss for the user it was made for,
    // while the auth owner converges on the actual Session.
    if (csrfRecovery === "isolated" && refreshed?.status === "different-user") {
      throw createSessionChangedError(error);
    }
    return apiFetch(path, options, {
      ...internal,
      hasRetried: true,
    });
  }
}
